import { Alert, Col, Form, Row } from "react-bootstrap";
import type { WasteMovementFormValues } from "../model/formValues";
import {
    BROKER_DEALER_ADDRESS_MAX_LENGTH,
    BROKER_DEALER_EMAIL_MAX_LENGTH,
    BROKER_DEALER_NAME_MAX_LENGTH,
    BROKER_DEALER_PHONE_MAX_LENGTH,
    BROKER_DEALER_POSTCODE_MAX_LENGTH,
    BROKER_DEALER_REGISTRATION_MAX_LENGTH,
    isValidBrokerDealerPostcode,
    needsBrokerDealerRemovalConfirmation,
    normalizeBrokerDealerPostcode,
    type BrokerDealerErrors,
} from "../core/wasteMovementBrokerDealer";

type BrokerDealerTextField =
    | "brokerDealerOrganisationName"
    | "brokerDealerRegistrationNumber"
    | "brokerDealerFullAddress"
    | "brokerDealerPostcode"
    | "brokerDealerEmailAddress"
    | "brokerDealerPhoneNumber";

export type BrokerDealerSectionProps = {
    values: WasteMovementFormValues;
    errors: BrokerDealerErrors;
    /** Same visibility rule as the rest of the form (persisted record, after a save attempt, or live validation). */
    showErrors: boolean;
    readOnlyForm: boolean;
    isNew: boolean;
    /** Whether the saved record has a broker/dealer (removing it needs an explicit confirmation). */
    hasStored: boolean;
    requiredMarker: JSX.Element | null;
    updateField: (field: keyof WasteMovementFormValues, value: string) => void;
    applyValues?: (updater: (current: WasteMovementFormValues) => WasteMovementFormValues) => void;
};

function ErrorText({ message, show }: { message?: string; show: boolean }) {
    if (!show || !message) return null;
    return <div className="invalid-feedback d-block">{message}</div>;
}

/**
 * S1D section of the Waste Received form: whether a broker or dealer arranged the movement and, if so, their details
 * (DEFRA `brokerOrDealer`). Answering No after one was saved needs an explicit confirmation before it is removed.
 */
export function BrokerDealerSection(props: BrokerDealerSectionProps) {
    const { values, errors, showErrors, readOnlyForm, isNew, hasStored, requiredMarker, updateField, applyValues } = props;
    const involved = values.brokerDealerInvolved;
    const invalid = (field: keyof BrokerDealerErrors) => (showErrors && errors[field] ? " is-invalid" : "");
    const textControl = (
        field: BrokerDealerTextField,
        options: { id: string; maxLength: number; type?: string; placeholder?: string; onBlur?: () => void }
    ) => (
        <Form.Control
            id={options.id}
            type={options.type ?? "text"}
            maxLength={options.maxLength}
            value={values[field]}
            placeholder={options.placeholder}
            onChange={(e) => updateField(field, e.target.value)}
            onBlur={options.onBlur}
            className={`form-control${invalid(field)}`}
            disabled={readOnlyForm}
        />
    );
    const addressGiven = values.brokerDealerFullAddress.trim() !== "";

    return (
        <div className="waste-movement-form-panel mb-3" data-testid="broker-dealer-section">
            <div className="fw-semibold mb-2">Broker or dealer</div>
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-broker-dealer-involved">
                            Was a broker or dealer involved? {requiredMarker}
                        </Form.Label>
                        <Form.Select
                            id="waste-movement-broker-dealer-involved"
                            value={involved}
                            onChange={(e) => updateField("brokerDealerInvolved", e.target.value)}
                            className={`form-select${invalid("brokerDealerInvolved")}`}
                            disabled={readOnlyForm}
                        >
                            <option value="">Select…</option>
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </Form.Select>
                        <div className="form-text">Answer Yes if a broker or dealer acted as the intermediary arranging this movement.</div>
                        <ErrorText message={errors.brokerDealerInvolved} show={showErrors} />
                    </Form.Group>
                </Col>
            </Row>
            {needsBrokerDealerRemovalConfirmation(values, { isNew, hasStored }) && (
                <Alert variant="warning" className="py-2 mb-3" data-testid="broker-dealer-removal-confirmation">
                    <div className="mb-2">
                        This movement has a broker or dealer recorded
                        {values.brokerDealerOrganisationName.trim() ? ` (${values.brokerDealerOrganisationName.trim()})` : ""}. You have
                        answered No, so it will be removed when you save. To keep it, answer Yes.
                    </div>
                    <Form.Check
                        type="checkbox"
                        id="waste-movement-confirmRemoveBrokerDealer"
                        label="Remove the recorded broker or dealer"
                        checked={values.confirmRemoveBrokerDealer}
                        disabled={readOnlyForm || !applyValues}
                        onChange={(e) => applyValues?.((current) => ({ ...current, confirmRemoveBrokerDealer: e.target.checked }))}
                    />
                    <ErrorText message={errors.confirmRemoveBrokerDealer} show={showErrors} />
                </Alert>
            )}
            {involved === "true" && (
                <div data-testid="broker-dealer-details">
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-name">
                                    Broker or dealer organisation name {requiredMarker}
                                </Form.Label>
                                {textControl("brokerDealerOrganisationName", {
                                    id: "waste-movement-broker-dealer-name",
                                    maxLength: BROKER_DEALER_NAME_MAX_LENGTH,
                                })}
                                <ErrorText message={errors.brokerDealerOrganisationName} show={showErrors} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-registration">Registration number</Form.Label>
                                {textControl("brokerDealerRegistrationNumber", {
                                    id: "waste-movement-broker-dealer-registration",
                                    maxLength: BROKER_DEALER_REGISTRATION_MAX_LENGTH,
                                    placeholder: "e.g. CBDU123456",
                                })}
                                <div className="form-text">Their waste carrier, broker and dealer registration, if known.</div>
                                <ErrorText message={errors.brokerDealerRegistrationNumber} show={showErrors} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-address">Address</Form.Label>
                                <Form.Control
                                    id="waste-movement-broker-dealer-address"
                                    as="textarea"
                                    rows={2}
                                    maxLength={BROKER_DEALER_ADDRESS_MAX_LENGTH}
                                    value={values.brokerDealerFullAddress}
                                    onChange={(e) => updateField("brokerDealerFullAddress", e.target.value)}
                                    className={`form-control${invalid("brokerDealerFullAddress")}`}
                                    disabled={readOnlyForm}
                                />
                                <ErrorText message={errors.brokerDealerFullAddress} show={showErrors} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-postcode">
                                    Postcode {addressGiven ? requiredMarker : null}
                                </Form.Label>
                                {textControl("brokerDealerPostcode", {
                                    id: "waste-movement-broker-dealer-postcode",
                                    maxLength: BROKER_DEALER_POSTCODE_MAX_LENGTH,
                                    placeholder: "UK postcode or Eircode",
                                    onBlur: () => {
                                        const normalized = normalizeBrokerDealerPostcode(values.brokerDealerPostcode);
                                        if (isValidBrokerDealerPostcode(normalized) && normalized !== values.brokerDealerPostcode)
                                            updateField("brokerDealerPostcode", normalized);
                                    },
                                })}
                                <ErrorText message={errors.brokerDealerPostcode} show={showErrors} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-email">Email address</Form.Label>
                                {textControl("brokerDealerEmailAddress", {
                                    id: "waste-movement-broker-dealer-email",
                                    type: "email",
                                    maxLength: BROKER_DEALER_EMAIL_MAX_LENGTH,
                                })}
                                <ErrorText message={errors.brokerDealerEmailAddress} show={showErrors} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-broker-dealer-phone">Phone number</Form.Label>
                                {textControl("brokerDealerPhoneNumber", {
                                    id: "waste-movement-broker-dealer-phone",
                                    type: "tel",
                                    maxLength: BROKER_DEALER_PHONE_MAX_LENGTH,
                                })}
                                <ErrorText message={errors.brokerDealerPhoneNumber} show={showErrors} />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
}
